import { Box, Button, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { getFormValidation } from "src/Shared/Components";
import { EDITOR_TYPES } from "src/Shared/Enums";
import { fields } from "./fields";
import { IGridFormControlProps } from "src/Shared/Interfaces/IGridFormControlProps.interface";
import GridFormControl from "src/Shared/Components/GridFormControl/GridFormControl";
import MainContentBox from "src/Shared/Components/MainContentBox/MainContentBox";
import { observer } from "mobx-react";
import { useStyles } from "./AddOrEditCompany.styles";
import Title from "src/Shared/Components/Title/Title";
import { useStore } from "src/Shared/Hooks/UseStore.hook";
import { ICompany, IRouteParams } from "../../Interfaces";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { VIEW_MODE } from "src/Shared/Enums/ViewMode.enum";
import CompanyService from "../../Services/Company.service";
import { finalize } from "rxjs";
import { AxiosError } from "axios";
import { ToastService } from "src/Shared/Services";
import { IResult } from "src/Shared/Interfaces";
import { useModal } from "src/Shared/Hooks";
import DateTimeUtils from "src/Shared/Tools/DateTimeUtils";
import SlaComment from "../SlaComment/SlaComment";
import { VendorType } from "../../Enums/VendorType";
import { AttachmentTypeEnum } from "src/Shared/Enums/AttachmentType.enum";
import CompanySLAInfo from "../CompanySLAInfo/CompanySLAInfo";
import CompanyComments from "../CompanyComments/CompanyComments";
import ImperansCompanyIntegration from "../ImperansCompanyIntegration/ImperansCompanyIntegration";
import { CompanyIntegrationStatus } from "../../Enums/CompanyIntegrationStatus.enum";
import ConditionalWrapper from "src/Shared/Components/ConditionalWrapper/ConditionalWrapper";
import { ClientStatus } from "../../Enums/ClientStatus.enum";
import IndustryInfoDialog from "./IndustryInfoDialog";

type Props = {
  subMenuRender?: boolean;
};

const AddOrEditCompany: React.FC<Props> = ({ subMenuRender }) => {
  const modal = useModal();
  const companyForm = useMemo(() => getFormValidation(fields), []);
  const getField = (key: string) => companyForm.$(key);
  const navigate = useNavigate();
  const classes = useStyles();
  const [mode, setMode] = useState<VIEW_MODE>();
  const {
    rootStore: {
      companyStore: {
        company,
        getCompany,
        resetCompanyList,
        getArchivedCompany,
        archivedCompany,
        getCompanyOverview,
        getProspectsCount,
      },
      commonStore: {
        supportiveRegions,
        vendorTypes,
        industries,
        leadTypeList,
        serviceTypeList,
        clientStatus,
        franchiseOffices,
        slaStatus,
        loadCommonData,
      },
      uiStore,
    },
  } = useStore();

  const fieldRules: Record<string, string> = {
    franchiseID: "required",
    franchiseOfficeID: "required",
    companyType: "required",
    vendorTypeIDs: "required",
    industryID: "required",
    website: "string|url",
    leadTypeID: "required",
    serviceTypeIDs: "required",
    primaryCompanyRelationshipManager: "required",
  };
  const [title, setTitle] = useState<string>("Add New Client");
  const [buttonText, setbuttonText] = useState<string>("Add Company");

  let { viewMode, companyRowID } = useParams<Partial<IRouteParams>>();
  const loc = useLocation();

  useEffect(() => {
    loadCommonData();

    let newMode = viewMode;

    if (subMenuRender) {
      if (loc.pathname.includes("/add")) {
        newMode = VIEW_MODE.ADD;
      } else if (loc.pathname.includes("/archived")) {
        newMode = VIEW_MODE.Archived;
      } else {
        newMode = VIEW_MODE.EDIT;
      }
    }

    setMode(newMode);
  }, [loc.pathname, subMenuRender, viewMode]);

  useEffect(() => {
    switch (mode) {
      case VIEW_MODE.ADD:
        setTitle("Add New Client");
        break;

      case VIEW_MODE.EDIT:
        setbuttonText("Update Company");
        getCompany(companyRowID);
        break;

      case VIEW_MODE.Archived:
        setbuttonText("Transfer Company");
        setTitle("Archived Client");
        getArchivedCompany(Number(companyRowID));
        break;
    }
  }, [mode, companyRowID]);

  useEffect(() => {
    if (company && mode == VIEW_MODE.EDIT) {
      setTitle(`${company.companyName} - Edit`);
      companyForm.set(company);
      updateValidation();
    }
  }, [company]);

  useEffect(() => {
    if (archivedCompany && mode == VIEW_MODE.Archived) {
      if (archivedCompany.serviceTypeIDs == null) {
        archivedCompany.serviceTypeIDs = [];
      }
      companyForm.set(archivedCompany);


      // Set default values
      if (getField("specialPricing").value == null) {
        getField("specialPricing").set("value", false);
      }

      getField("reasonForTransfer").set("rules", "required");
      companyForm.showErrors();
      // companyForm.validate();
    }
  }, [archivedCompany]);

  useEffect(() => {
    updateValidation();
  }, [getField("clientStatusID").value]);

  const onCurrentSLAChange = (value) => {
    modal.open(<SlaComment companyForm={companyForm} />);
  };
  const onFrachiseChange = (value: number) => {
    if (value) {
      // getField('franchiseOfficeID').set(
      //   'options',
      //   franchiseOffices.filter((x) => x.franchiseID == value)
      // );
      let primaryOffice = franchiseOffices.find(
        (x) => x.franchiseID == value && x.isPrimary === true,
      );
      if (primaryOffice) {
        getField("franchiseOfficeID").set("value", primaryOffice.value);
      } else {
        getField("franchiseOfficeID").set("value", null);
      }
    }
  };

  const onClientStatusChange = (value: number) => {
    if (value) {
      updateValidation();
    }
  };

  const healthTickManagerVisiblity = () => {
    let isVisible = getField("vendorTypeIDs").value?.some(
      (x) => x == VendorType.Healthtick,
    );
    if (!isVisible) {
      getField("healthTickPrimaryRelationshipManager").set("value", null);
    }
    return isVisible;
  };

  useEffect(() => {
    let vendorTypes = getField("vendorTypeIDs").value;
    if (!vendorTypes?.some((x) => x == VendorType.TDDA)) {
      getField("tddaAttachments").set("value", []);
    }
    if (!vendorTypes?.some((x) => x == VendorType.Healthtick)) {
      getField("healthTickAttachments").set("value", []);
    }
  }, [getField("vendorTypeIDs").value]);

  const gridProps: IGridFormControlProps = {
    row: 2,
    col: 13,
    spacing: 4,
    itemResponsive: {
      lg: 6,
      md: 6,
      sm: 12,
    },
    inputControls: [
      {
        field: getField("companyName"),
        fieldKey: "companyName",
        type: EDITOR_TYPES.TEXT_FIELD,
      },
      {
        field: getField("clientStatusID"),
        fieldKey: "clientStatusID",
        type: EDITOR_TYPES.DROPDOWN,
        // Once an onboarding has been submitted there is no route back to Prospect.
        options:
          mode == VIEW_MODE.EDIT &&
          company?.hasSubmittedOnboarding &&
          company?.clientStatusID != ClientStatus.Prospect
            ? clientStatus.filter((x) => x.value != ClientStatus.Prospect)
            : clientStatus,
        onValueChange(clientStatus, _) {
          onClientStatusChange(clientStatus);
        },
      },
      {
        field: getField("franchiseID"),
        fieldKey: "franchiseID",
        type: EDITOR_TYPES.AUTO_COMPLETE,
        options: supportiveRegions,
        onValueChange(franchise, _) {
          onFrachiseChange(franchise.value);
        },
      },
      {
        field: getField("franchiseOfficeID"),
        fieldKey: "franchiseOfficeID",
        type: EDITOR_TYPES.AUTO_COMPLETE,
        options:
          franchiseOffices.filter(
            (x) => x.franchiseID == getField("franchiseID").value,
          ) || [],
      },
      {
        field: getField("companyType"),
        fieldKey: "companyType",
        type: EDITOR_TYPES.RADIO_BUTTON,
        options: [
          {
            label: "National",
            value: "National",
          },
          {
            label: "Regional",
            value: "Regional",
          },
        ],
      },

      {
        field: getField("vendorTypeIDs"),
        fieldKey: "vendorTypeIDs",
        type: EDITOR_TYPES.DROPDOWN,
        multiple: true,
        options: vendorTypes,
      },
      {
        field: getField("specialPricing"),
        fieldKey: "specialPricing",
        type: EDITOR_TYPES.RADIO_BUTTON,
        options: [
          {
            label: "Yes",
            value: true,
          },
          {
            label: "No",
            value: false,
          },
        ],
      },
      {
        field: getField("industryID"),
        fieldKey: "industryID",
        type: EDITOR_TYPES.DROPDOWN,
        options: industries,
        infoIcon: true,
        onInfoIconClick() {
          modal.open(
            <IndustryInfoDialog
              industries={industries}
              selectedIndustryId={getField("industryID").value}
            />
          );
        },
      },
      {
        field: getField("website"),
        fieldKey: "website",
        type: EDITOR_TYPES.TEXT_FIELD,
      },
      {
        field: getField("totalStaffNumber"),
        fieldKey: "totalStaffNumber",
        type: EDITOR_TYPES.TEXT_FIELD,
        inputProps: { min: 0 },
      },
      {
        field: getField("leadTypeID"),
        fieldKey: "leadTypeID",
        type: EDITOR_TYPES.DROPDOWN,
        options: leadTypeList.filter(
          (x) => x.isActive || x.value === getField("leadTypeID").value,
        ),
      },
      {
        field: getField("serviceTypeIDs"),
        fieldKey: "serviceTypeIDs",
        type: EDITOR_TYPES.DROPDOWN,
        multiple: true,
        options: serviceTypeList,
      },
      {
        field: getField("primaryCompanyRelationshipManager"),
        fieldKey: "primaryCompanyRelationshipManager",
        type: EDITOR_TYPES.AUTO_COMPLETE_ASYNC,
      },
      {
        field: getField("healthTickPrimaryRelationshipManager"),
        fieldKey: "healthTickPrimaryRelationshipManager",
        type: EDITOR_TYPES.AUTO_COMPLETE_ASYNC,
        hidden: !healthTickManagerVisiblity(),
      },
      {
        field: getField("additionalCompanyRelationshipManagers"),
        fieldKey: "additionalCompanyRelationshipManagers",
        type: EDITOR_TYPES.AUTO_COMPLETE_ASYNC,
        multiple: true,
      },
      {
        field: getField("tddaAttachments"),
        fieldKey: "tddaAttachments",
        multiple: true,
        type: EDITOR_TYPES.FILE_UPLOADER,
        attachmentType: AttachmentTypeEnum.TDDA,
        hidden: !getField("vendorTypeIDs").value?.some(
          (x) => x == VendorType.TDDA,
        ),
      },
      {
        field: getField("healthTickAttachments"),
        fieldKey: "healthTickAttachments",
        multiple: true,
        type: EDITOR_TYPES.FILE_UPLOADER,
        attachmentType: AttachmentTypeEnum.HealthTick,
        hidden: !getField("vendorTypeIDs").value?.some(
          (x) => x == VendorType.Healthtick,
        ),
      },
      {
        field: getField("salesTotalValue"),
        fieldKey: "salesTotalValue",
        type: EDITOR_TYPES.TEXT_FIELD,
        isEditingMode: false,
        hidden: mode == VIEW_MODE.ADD,
      },
      {
        field: getField("dealStage"),
        fieldKey: "dealStage",
        type: EDITOR_TYPES.TEXT_FIELD,
        isEditingMode: false,
        hidden: mode == VIEW_MODE.ADD,
      },
      {
        field: getField("createdByOn"),
        fieldKey: "createdByOn",
        type: EDITOR_TYPES.TEXT_FIELD,
        isEditingMode: false,
        hidden: mode == VIEW_MODE.ADD,
      },
      {
        field: getField("modifiedByOn"),
        fieldKey: "modifiedByOn",
        type: EDITOR_TYPES.TEXT_FIELD,
        isEditingMode: false,
        hidden: mode == VIEW_MODE.ADD,
      },
      {
        field: getField("importedByOn"),
        fieldKey: "importedByOn",
        type: EDITOR_TYPES.TEXT_FIELD,
        isEditingMode: false,
        hidden:
          mode == VIEW_MODE.ADD || !Boolean(getField("importedByOn").value),
      },
      {
        field: getField("reasonForTransfer"),
        fieldKey: "reasonForTransfer",
        type: EDITOR_TYPES.TEXT_FIELD,
        multiline: true,
        rows: 2,
        hidden: mode != VIEW_MODE.Archived,
      },
    ],
    form: companyForm,
  };

  const onFromSubmit = () => {
    if (!companyForm.isValid) {
      companyForm.validate();
      companyForm.showErrors();
      return;
    }
    const request: ICompany = { ...companyForm.values() };
    request.attachments = [
      ...request.tddaAttachments,
      ...request.healthTickAttachments,
    ];
    switch (mode) {
      case VIEW_MODE.ADD:
        uiStore.setGlobalLoader(true);
        CompanyService.addCompany(request)
          .pipe(
            finalize(() => {
              uiStore.setGlobalLoader(false);
            }),
          )
          .subscribe({
            next: (data: IResult<string>) => {
              resetCompanyList();
              getProspectsCount();
              ToastService.success(data.message);
              if (!subMenuRender) {
                navigate(`/company/${data.content}/addContacts`);
              } else {
                navigate(`/company/${data.content}/details`);
              }
            },
            error: (error: AxiosError) => ToastService.axiosError(error),
          });
        break;
      case VIEW_MODE.EDIT:
        uiStore.setGlobalLoader(true);
        CompanyService.updateCompany(request)
          .pipe(
            finalize(() => {
              uiStore.setGlobalLoader(false);
            }),
          )
          .subscribe({
            next: (data: IResult<string>) => {
              resetCompanyList();
              getProspectsCount();
              ToastService.success(data.message);
              if (!subMenuRender) {
                navigate(`/company`);
              } else {
                getCompanyOverview(companyRowID);
              }
            },
            error: (error: AxiosError) => ToastService.axiosError(error),
          });
        break;
      case VIEW_MODE.Archived:
        uiStore.setGlobalLoader(true);
        request.archivedCRMDataID = Number(companyRowID);
        request.isArchived = true;
        CompanyService.addCompany(request)
          .pipe(
            finalize(() => {
              uiStore.setGlobalLoader(false);
            }),
          )
          .subscribe({
            next: (data: IResult<string>) => {
              resetCompanyList();
              getProspectsCount();
              ToastService.success(data.message);
              navigate(`/company/${data.content}/details`);
            },
            error: (error: AxiosError) => ToastService.axiosError(error),
          });
        break;
    }
  };

  const handleOpen = (isSlaRelated: boolean) => {
    modal.open(
      <CompanyComments
        isSlaRelated={isSlaRelated}
        companyRowID={companyRowID}
      />,
    );
  };

  const viewSlaInfo = () => {
    modal.open(
      <CompanySLAInfo
        companyRowID={companyRowID}
        primaryCompanyRelationshipManager={
          getField("primaryCompanyRelationshipManager").value
        }
      />,
    );
  };

  const handleImperansIntegrationOpen = () => {
    modal.open(
      <ImperansCompanyIntegration
        companyRowID={companyRowID}
        companyIntergrationDeatail={company.companyIntegrationDetail}
        title={
          company &&
          company.companyIntegrationStatusID ==
            CompanyIntegrationStatus.LinkedToImperans
            ? "Edit Imperans Link"
            : "Link Company to Imperans"
        }
      />,
    );
  };

  const updateValidation = () => {
    let clientStatusID = getField("clientStatusID").value;

    // Prospect-stage clients (Prospect and Onboarding) save with the minimal field set —
    // only the company name is required.
    if (
      clientStatusID == ClientStatus.Prospect ||
      clientStatusID == ClientStatus.Onboarding
    ) {
      Object.entries(fieldRules).forEach(([fieldName, rules]) => {
        getField(fieldName).set("rules", "");
      });
    } else {
      Object.entries(fieldRules).forEach(([fieldName, rules]) => {
        getField(fieldName).set("rules", rules);
      });
    }
    companyForm.validate();
    companyForm.showErrors();
  };

  return (
    <>
      <ConditionalWrapper
        condition={!subMenuRender}
        wrapper={(children) => (
          <MainContentBox>
            <Box className={classes.viewButtons}>
              <Title backButtonEnabled warningRequired title={title}></Title>
              {mode == VIEW_MODE.EDIT && (
                <Box>
                  <Button
                    sx={{ mr: 2 }}
                    variant="contained"
                    onClick={() => handleImperansIntegrationOpen()}
                  >
                    {company &&
                    company.companyIntegrationStatusID ==
                      CompanyIntegrationStatus.LinkedToImperans
                      ? "Edit Imperans Link"
                      : "Link Company to Imperans"}
                  </Button>
                  <Button
                    sx={{ mr: 2 }}
                    variant="contained"
                    component={Link}
                    to={`/company/${companyRowID}/contacts`}
                  >
                    View/Edit Contacts
                  </Button>
                  <Button
                    sx={{ mr: 2 }}
                    variant="contained"
                    component={Link}
                    to={`/company/${companyRowID}/offices`}
                  >
                    View/Edit Offices
                  </Button>
                  <Button
                    sx={{ mr: 2 }}
                    variant="contained"
                    onClick={() => handleOpen(false)}
                  >
                    View/Edit Comments
                  </Button>
                  <Button variant="contained" onClick={() => viewSlaInfo()}>
                    View SLA Status
                  </Button>
                </Box>
              )}
            </Box>
            {children}
            <Box className={classes.submitButtonBox}>
              <Button variant="contained" onClick={onFromSubmit}>
                {buttonText}
              </Button>
            </Box>
          </MainContentBox>
        )}
      >
        {subMenuRender && (
          <Box className={classes.subMenuHeader}>
            <Typography className={classes.title} variant="h5">
              Details
            </Typography>
            <Button variant="contained" onClick={onFromSubmit}>
              Save Changes
            </Button>
          </Box>
        )}
        <Box className={classes.root}>
          <GridFormControl {...gridProps} />
        </Box>
      </ConditionalWrapper>
    </>
  );
};

export default observer(AddOrEditCompany);
